// @core/stores/billing/BillingInvoiceStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  CreateInvoiceRequest,
  FinalizeInvoiceRequest,
  FinalizeInvoiceResponse,
  Invoice,
  PayInvoiceRequest,
  PayInvoiceResponse,
  UpdateInvoiceRequest,
  VoidInvoiceRequest,
  VoidInvoiceResponse,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingInvoiceStore {
  // List state
  items: Invoice[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Invoice | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Invoice>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Invoice | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Invoice | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create invoice
   */
  async CreateInvoice(data: CreateInvoiceRequest): Promise<Invoice> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateInvoice(data);
      const item = response.invoice;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Finalize invoice (assign number, prevent edits)
   */
  async FinalizeInvoice(id: string, data: FinalizeInvoiceRequest): Promise<FinalizeInvoiceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.FinalizeInvoice(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get invoice by ID
   */
  async GetInvoice(id: string): Promise<Invoice> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetInvoice(id);
      const item = response.invoice;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List invoices
   */
  async ListInvoices(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListInvoices(params);

      runInAction(() => {
        this.items = response.invoices?.invoices ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pay invoice
   */
  async PayInvoice(id: string, data: PayInvoiceRequest): Promise<PayInvoiceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.PayInvoice(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update invoice (draft only)
   */
  async UpdateInvoice(id: string, data: UpdateInvoiceRequest): Promise<Invoice> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.UpdateInvoice(id, data);
      const item = response.invoice;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Void invoice
   */
  async VoidInvoice(id: string, data: VoidInvoiceRequest): Promise<VoidInvoiceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.VoidInvoice(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const billingInvoiceStore = new BillingInvoiceStore();
